import { useCallback, useEffect, useRef, useState } from "react";

type UseUnreadOptions = {
  userId?: string;
};

/**
 * Per-channel unread counts, the "already notified" throttle map, and the
 * unread-divider marker — plus the localStorage persistence and the desktop/
 * Android badge-count effect that hang off them (step 3.2).
 *
 * Keys are fully-qualified "space:channel" ids built by the callers' fq/k
 * helpers — this hook never constructs keys itself, it just stores what it is
 * given (do not change the format; see Known landmines).
 *
 * There are no unread-specific socket handlers: unread is bumped by
 * ChatLayout's onNew/onSpaceNotify message handlers (which mostly do
 * non-unread work, so they stay put) through the returned markUnread /
 * setNotified, and cleared parent-side by markChannelRead, switch/DM-open
 * navigation, and the jump-to-present / near-bottom scroll handlers via the
 * returned setters.
 */
export function useUnread({ userId }: UseUnreadOptions) {
  const [unread, setUnread] = useState<Record<string, number>>({});
  const unreadUserRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = userId || "";
    if (!uid) return;
    if (unreadUserRef.current === uid) return;
    unreadUserRef.current = uid;
    setUnread({});
    try {
      const raw = localStorage.getItem(`unreadCounts:${uid}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") {
        setUnread((prev) => {
          const next: Record<string, number> = { ...parsed };
          for (const [k, v] of Object.entries(prev)) {
            const pv = Number(v) || 0;
            const sv = Number((next as any)[k] || 0);
            if (pv > sv) next[k] = pv;
          }
          return next;
        });
      }
    } catch {
      /* noop */
    }
  }, [userId]);
  useEffect(() => {
    const uid = userId || "";
    if (!uid) return;
    // Persist only live (positive) counts. Reading a channel sets its entry to 0
    // rather than deleting it, so without this prune the stored map would grow
    // unbounded with dead `space:channel -> 0` keys (and keep orphaned spaces
    // around) until the next successful server hydration replaces it wholesale.
    try {
      const live: Record<string, number> = {};
      for (const [key, value] of Object.entries(unread)) {
        if ((value || 0) > 0) live[key] = value;
      }
      localStorage.setItem(`unreadCounts:${uid}`, JSON.stringify(live));
    } catch { /* noop */ }
  }, [userId, unread]);
  useEffect(() => {
    const total = Object.values(unread).reduce((s, n) => s + (n || 0), 0);
    // Electron: send count + a canvas-drawn badge image for Windows taskbar overlay
    if ((window as any).echoDesktop?.setBadgeCount) {
      let overlayDataUrl: string | undefined;
      try {
        if (total > 0) {
          const canvas = document.createElement('canvas');
          canvas.width = 16; canvas.height = 16;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ef4444';
            ctx.beginPath(); ctx.arc(8, 8, 8, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 9px -apple-system,system-ui,sans-serif';
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(total > 99 ? '99+' : String(total), 8, 8.5);
            overlayDataUrl = canvas.toDataURL('image/png');
          }
        }
      } catch { /* noop */ }
      (window as any).echoDesktop.setBadgeCount(total, overlayDataUrl);
    }
    // Android
    if ((window as any).EchoAndroid?.setBadgeCount) {
      (window as any).EchoAndroid.setBadgeCount(total);
    }
  }, [unread]);
  const markUnread = useCallback((fid: string) => {
    setUnread((prev) => ({ ...prev, [fid]: (prev[fid] || 0) + 1 }));
  }, []);
  // Throttle notifications: one per channel until user visits it. Persisted
  // per-account (like unreadCounts above) and reset when the user changes, so a
  // shared browser doesn't inherit another account's "already notified"
  // suppressions for overlapping channels.
  const [notified, setNotified] = useState<Record<string, boolean>>({});
  const notifiedUserRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = userId || "";
    if (!uid) return;
    if (notifiedUserRef.current === uid) return;
    notifiedUserRef.current = uid;
    setNotified({});
    try {
      const raw = localStorage.getItem(`notifiedChannels:${uid}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") {
        setNotified((prev) => {
          const next: Record<string, boolean> = { ...parsed };
          for (const [kk, v] of Object.entries(prev)) {
            if (v) next[kk] = true;
          }
          return next;
        });
      }
    } catch {
      /* noop */
    }
  }, [userId]);
  useEffect(() => {
    const uid = userId || "";
    if (!uid) return;
    try { localStorage.setItem(`notifiedChannels:${uid}`, JSON.stringify(notified)); } catch { /* noop */ }
  }, [userId, notified]);
  const notifiedRef = useRef<Record<string, boolean>>({});
  useEffect(() => { notifiedRef.current = notified; }, [notified]);
  const [unreadMarkerByChan, setUnreadMarkerByChan] = useState<Record<string, string | null>>({});

  return {
    unread,
    setUnread,
    markUnread,
    notified,
    setNotified,
    notifiedRef,
    unreadMarkerByChan,
    setUnreadMarkerByChan,
  };
}


